import React, { useState } from 'react';

interface GalleryItemProps {
  id: string;
  title: string;
  category: string;
  description: string;
  image: string;
}

const GalleryItem: React.FC<GalleryItemProps> = ({ id, title, category, description, image }) => {
  return (
    <div className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300">
      <div className="aspect-[4/3] bg-gray-200 relative overflow-hidden">
        <img 
          src={image} 
          alt={title}
          className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute top-3 left-3 bg-gradient-to-r from-secondary to-primary text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg">
          {category}
        </div>
      </div>
      <div className="p-5">
        <h3 className="text-lg font-serif font-bold mb-2 text-gray-800">{title}</h3>
        <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  );
};

const Gallery: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState('todos');
  
  const galleryItems = [
    {
      id: '1',
      title: 'Bioestimulador de Colágeno',
      category: 'Bioestimuladores',
      description: 'Tratamento com bioestimuladores para rejuvenescimento e firmeza da pele, estimulando a produção natural de colágeno.',
      image: '/assets/img/case/Bioestimulador%20de%20Colageno.jpeg',
    },
    {
      id: '2',
      title: 'Blefaroplastia',
      category: 'Cirurgia',
      description: 'Cirurgia de rejuvenescimento das pálpebras para correção de bolsas e flacidez, resultando em olhar mais jovem e descansado.',
      image: '/assets/img/case/Blefaroplastia.JPEG',
    },
    {
      id: '3',
      title: 'Toxina Botulínica',
      category: 'Toxina Botulínica',
      description: 'Aplicação de toxina botulínica para suavizar linhas de expressão e prevenir rugas dinâmicas.',
      image: '/assets/img/case/Botox.jpeg',
    },
    {
      id: '4',
      title: 'Lift Facial',
      category: 'Cirurgia',
      description: 'Procedimento de lifting facial para rejuvenescimento e reposicionamento dos tecidos faciais.',
      image: '/assets/img/case/Lift%20Facial.jpeg',
    },
    {
      id: '5',
      title: 'Lipo de Papada',
      category: 'Harmonização',
      description: 'Lipo de papada para definir o contorno do pescoço e melhorar o perfil facial.',
      image: '/assets/img/case/Lipo%20de%20Papada.jpeg',
    },
    {
      id: '6',
      title: 'Otomodelação',
      category: 'Harmonização',
      description: 'Correção estética das orelhas sem cirurgia, promovendo harmonia facial.',
      image: '/assets/img/case/Otomodelação.jpeg',
    },
    {
      id: '7',
      title: 'Preenchimento de Mandíbula',
      category: 'Preenchimento',
      description: 'Preenchimento para definição do contorno mandibular e harmonização do terço inferior da face.',
      image: '/assets/img/case/Preenchimento%20de%20Mandibula.jpeg',
    },
    {
      id: '8',
      title: 'Preenchimento Labial',
      category: 'Preenchimento',
      description: 'Preenchimento labial com ácido hialurônico para volume e contorno natural dos lábios.',
      image: '/assets/img/case/Preenchimento%20Labial.JPEG',
    },
    {
      id: '9',
      title: 'Rinomodelação',
      category: 'Harmonização',
      description: 'Remodelação nasal sem cirurgia através de preenchimento, corrigindo pequenas imperfeições.',
      image: '/assets/img/case/Rinomodelação.jpeg',
    },
    {
      id: '10',
      title: 'Terapia Capilar',
      category: 'Capilar',
      description: 'Tratamento avançado para restauração capilar e combate à queda de cabelo.',
      image: '/assets/img/case/Terapia%20Capilar.jpeg',
    },
  ];

  const filters = [
    { id: 'todos', name: 'Todos' },
    { id: 'Bioestimuladores', name: 'Bioestimuladores' },
    { id: 'Cirurgia', name: 'Cirurgia' },
    { id: 'Toxina Botulínica', name: 'Toxina Botulínica' },
    { id: 'Harmonização', name: 'Harmonização' },
    { id: 'Preenchimento', name: 'Preenchimento' },
    { id: 'Capilar', name: 'Capilar' },
  ];

  const filteredItems = activeFilter === 'todos'
    ? galleryItems
    : galleryItems.filter(item => item.category === activeFilter);

  return (
    <section className="py-16">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h2 className="section-title">Galeria de Resultados</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Confira os resultados reais de nossos tratamentos estéticos. Todos os casos apresentados foram realizados em nossa clínica com o consentimento dos pacientes.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {filters.map(filter => (
            <button
              key={filter.id}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeFilter === filter.id
                  ? 'bg-primary text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => setActiveFilter(filter.id)}
            >
              {filter.name}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map(item => (
            <GalleryItem
              key={item.id}
              id={item.id}
              title={item.title}
              category={item.category}
              description={item.description}
              image={item.image}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Gallery;